import api from "../../../api";

const BASE = "api/prospecting-reports";

// ---------------------------------------------------------------- wizard data
// Comparable search. `kind` is one of sold | for_sale | leased | for_rent.
// Params: suburb, state, property_type, beds_min/max, baths_min/max,
// cars_min/max, price_min/max, radius_km, lat, lng, q, limit.
export const searchComparables = (params = {}) =>
  api.get(`${BASE}/comparables/`, { params });

export const getSuburbInsights = (params = {}) =>
  api.get(`${BASE}/suburb-insights/`, { params });

// Suggested sale range from the prices the agent selected.
export const getPriceIndication = (prices = []) =>
  api.post(`${BASE}/price-indication/`, { prices });

// ------------------------------------------------------------------------ CMA
export const getCmaReports = (params = {}) => api.get(`${BASE}/cma/`, { params });

export const getCmaReport = (id) => api.get(`${BASE}/cma/${id}/`);

export const createCmaReport = (data) => api.post(`${BASE}/cma/`, data);

export const updateCmaReport = (id, data) => api.patch(`${BASE}/cma/${id}/`, data);

export const deleteCmaReport = (id) => api.delete(`${BASE}/cma/${id}/`);

// Whole-list replace: the wizard step is a selection, so we send the final set.
export const setCmaComparables = (id, comparables) =>
  api.put(`${BASE}/cma/${id}/comparables/`, { comparables });

export const sendCmaReport = (id, data) => api.post(`${BASE}/cma/${id}/send/`, data);

// Results Summary + Select Records tabs, run from the report's Search Options.
export const getCmaRecords = (id) => api.get(`${BASE}/cma/${id}/records/`);

// Property Price Statement only: puts it on its public link once the form passes.
export const publishCmaReport = (id) => api.post(`${BASE}/cma/${id}/publish/`);

// Files the report's PDF into its property's Documents tab.
export const saveCmaToProperty = (id) => api.post(`${BASE}/cma/${id}/save-to-property/`);

// Returns a PDF blob — the caller is responsible for the object URL.
export const downloadCmaPdf = (id) =>
  api.get(`${BASE}/cma/${id}/pdf/`, { responseType: "blob" });

// --------------------------------------------------- market update templates
export const getMarketUpdateTemplates = (params = {}) =>
  api.get(`${BASE}/market-update/templates/`, { params });

export const getMergeTags = () => api.get(`${BASE}/market-update/templates/merge-tags/`);

export const createMarketUpdateTemplate = (data) =>
  api.post(`${BASE}/market-update/templates/`, data);

export const updateMarketUpdateTemplate = (id, data) =>
  api.patch(`${BASE}/market-update/templates/${id}/`, data);

export const deleteMarketUpdateTemplate = (id) =>
  api.delete(`${BASE}/market-update/templates/${id}/`);

// System templates are read-only; this is the only way to start from one.
export const duplicateMarketUpdateTemplate = (id, data = {}) =>
  api.post(`${BASE}/market-update/templates/${id}/duplicate/`, data);

// ----------------------------------------------------- market update reports
export const getMarketUpdateReports = (params = {}) =>
  api.get(`${BASE}/market-update/reports/`, { params });

export const createMarketUpdateReport = (data) =>
  api.post(`${BASE}/market-update/reports/`, data);

export const updateMarketUpdateReport = (id, data) =>
  api.patch(`${BASE}/market-update/reports/${id}/`, data);

export const deleteMarketUpdateReport = (id) =>
  api.delete(`${BASE}/market-update/reports/${id}/`);

// Pass force: true to deliberately send a second time — the API returns 409
// otherwise, so a double-click can never fan out to a client list twice.
export const sendMarketUpdate = (id, recipients, options = {}) =>
  api.post(`${BASE}/market-update/reports/${id}/send/`, { recipients, ...options });

// ------------------------------------------ property market update (Ignite flow)
// The agent's own Pipeline + Database owners. Params: source (all|pipeline|database),
// q, limit, offset.
export const getMarketUpdateContacts = (params = {}) =>
  api.get(`${BASE}/market-update/contacts/`, { params });

// Start a report for one contact: { source_type, source_id } or
// { source_type: "manual", full_address, suburb, state, postcode }.
export const createPropertyMarketUpdate = (data) =>
  api.post(`${BASE}/market-update/reports/`, { kind: "property", ...data });

export const getMarketUpdateReport = (id) =>
  api.get(`${BASE}/market-update/reports/${id}/`);

// The email HTML, SMS text and send history. withSender asks which number the
// texts would come from (a live provider call, so only the send step uses it).
export const getMarketUpdatePreview = (id, { withSender = false } = {}) =>
  api.get(`${BASE}/market-update/reports/${id}/preview/`, {
    params: withSender ? { with_sender: 1 } : {},
  });

// { channels: ["sms","email"], vendor_indexes, sms_message, subject, force }
export const sendPropertyMarketUpdate = (id, data) =>
  api.post(`${BASE}/market-update/reports/${id}/send/`, data);

// ------------------------------------------------------------ public (no auth)
// Hit by the vendor's browser from the emailed link. No token is required, and
// none should be sent — the share token in the URL is the credential.
export const getPublicCmaReport = (token) => api.get(`${BASE}/public/cma/${token}/`);

export const downloadPublicCmaPdf = (token) =>
  api.get(`${BASE}/public/cma/${token}/pdf/`, { responseType: "blob" });

export const getPublicMarketUpdate = (token) =>
  api.get(`${BASE}/public/market-update/${token}/`);

// ------------------------------------------------------------ DOMA Proposals
// Dynamic path segments (id, token) are always encodeURIComponent'd so a value
// containing "/" or "../" cannot retarget the request to another endpoint
// (client-side path traversal).
const seg = (v) => encodeURIComponent(String(v ?? ""));

export const getProposals = (params = {}) => api.get(`${BASE}/proposals/`, { params });

export const getProposal = (id) => api.get(`${BASE}/proposals/${seg(id)}/`);

// Create from a template, prefilled from an optional property / CMA / contact.
export const createProposal = (data) => api.post(`${BASE}/proposals/`, data);

// Autosave while a draft; pass base_revision for the optimistic-concurrency guard.
export const updateProposal = (id, data) => api.patch(`${BASE}/proposals/${seg(id)}/`, data);

export const deleteProposal = (id) => api.delete(`${BASE}/proposals/${seg(id)}/`);

// Pass force: true to deliberately re-send; the API returns 409 otherwise.
export const sendProposal = (id, data = {}) => api.post(`${BASE}/proposals/${seg(id)}/send/`, data);

export const setProposalOutcome = (id, listing_outcome) =>
  api.patch(`${BASE}/proposals/${seg(id)}/outcome/`, { listing_outcome });

export const getProposalEvents = (id) => api.get(`${BASE}/proposals/${seg(id)}/events/`);

export const downloadProposalPdf = (id) =>
  api.get(`${BASE}/proposals/${seg(id)}/pdf/`, { responseType: "blob" });

export const getProposalBlockTypes = () => api.get(`${BASE}/proposals/block-types/`);

// V2 AI writing assistant: generate/refine one block field. Does not persist.
export const proposalAi = (id, data) => api.post(`${BASE}/proposals/${seg(id)}/ai/`, data);

// V3 GeeVee Strategy Advisor: analyse the whole proposal → suggestions.
export const analyseProposalStrategy = (id) => api.post(`${BASE}/proposals/${seg(id)}/strategy/`, {});

// Save a proposal's design as a reusable agency template ("..." menu).
export const saveProposalAsTemplate = (id, data = {}) =>
  api.post(`${BASE}/proposals/${seg(id)}/save-as-template/`, data);

// Proply-parity workspace actions.
export const setProposalFavourite = (id, is_favourite) =>
  api.patch(`${BASE}/proposals/${seg(id)}/favourite/`, { is_favourite });
export const setProposalArchived = (id, is_archived) =>
  api.patch(`${BASE}/proposals/${seg(id)}/archive/`, { is_archived });
export const setProposalLabels = (id, label_ids) =>
  api.put(`${BASE}/proposals/${seg(id)}/labels/`, { label_ids });
export const setProposalCollaborators = (id, { user_ids = [], entire_team = false } = {}) =>
  api.put(`${BASE}/proposals/${seg(id)}/collaborators/`, { user_ids, entire_team });
export const getProposalTeammates = (params = {}) => api.get(`${BASE}/proposal-teammates/`, { params });

// CRM autofill (CG QA, 26 Oct): address search with owner details, the linked
// property's photos, and market stats from our sold data + cached Homer.
// Shared image library (skylines, Collings logos, plain colours, admin photos).
export const getProposalImageLibrary = () => api.get(`${BASE}/proposal-image-library/`);
export const addProposalLibraryImage = (file, label = "") => {
  const fd = new FormData();
  fd.append("file", file);
  if (label) fd.append("label", label);
  return api.post(`${BASE}/proposal-image-library/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const removeProposalLibraryImage = (id) => api.delete(`${BASE}/proposal-image-library/${seg(id)}/`);

export const searchProposalSubjects = (q) => api.get(`${BASE}/proposals/subject-search/`, { params: { q } });
export const getProposalPropertyPhotos = (id) => api.get(`${BASE}/proposals/${seg(id)}/property-photos/`);
export const getProposalMarketStats = (id) => api.get(`${BASE}/proposals/${seg(id)}/market-stats/`);

// Accepted proposal → draft e-sign listing authority, pre-filled from the proposal.
export const createProposalAuthority = (id, authority) =>
  api.post(`${BASE}/proposals/${seg(id)}/authority/`, { authority });

// Per-proposal analytics (views over time, unique visitors, per-section engagement, timeline).
export const getProposalAnalytics = (id) => api.get(`${BASE}/proposals/${seg(id)}/analytics/`);
// Absolute URL for the public page's engagement beacon (sent with fetch keepalive on hide/unload).
export const proposalEngagementUrl = (token) =>
  `${(api.defaults && api.defaults.baseURL) || ""}${BASE}/public/proposals/${seg(token)}/engagement/`;

export const getProposalLabels = () => api.get(`${BASE}/proposal-labels/`);
export const createProposalLabel = (data) => api.post(`${BASE}/proposal-labels/`, data);
export const updateProposalLabel = (id, data) => api.patch(`${BASE}/proposal-labels/${seg(id)}/`, data);
export const deleteProposalLabel = (id) => api.delete(`${BASE}/proposal-labels/${seg(id)}/`);

export const duplicateProposal = (id) => api.post(`${BASE}/proposals/${seg(id)}/duplicate/`, {});
export const setProposalOffline = (id, is_offline) =>
  api.patch(`${BASE}/proposals/${seg(id)}/publish/`, { is_offline });

// The public switch: turn the share link live/offline (publishes a draft, no email).
export const setProposalPublic = (id, isPublic) =>
  api.patch(`${BASE}/proposals/${seg(id)}/publish/`, { public: isPublic });

export const getProposalNotificationFeed = () => api.get(`${BASE}/proposal-notification-feed/`);
export const markProposalNotificationRead = (id) =>
  api.patch(`${BASE}/proposal-notification-feed/${seg(id)}/read/`, {});
export const markAllProposalNotificationsRead = () =>
  api.post(`${BASE}/proposal-notification-feed/read-all/`, {});

export const getProposalSections = () => api.get(`${BASE}/proposal-sections/`);
export const createProposalSection = (data) => api.post(`${BASE}/proposal-sections/`, data);
export const deleteProposalSection = (id) => api.delete(`${BASE}/proposal-sections/${seg(id)}/`);

export const getProposalNotificationPrefs = () => api.get(`${BASE}/proposal-notifications/`);
export const updateProposalNotificationPrefs = (data) => api.patch(`${BASE}/proposal-notifications/`, data);
export const getProposalWorkspaceSettings = () => api.get(`${BASE}/proposal-workspace-settings/`);
export const updateProposalWorkspaceSettings = (data) => api.patch(`${BASE}/proposal-workspace-settings/`, data);

// Upload an image (hero/gallery/agent) → { url }. Validated server-side too.
export const uploadProposalImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`${BASE}/proposals/upload-image/`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Upload a video (MP4/WEBM/MOV) for the embed block → { url }.
export const uploadProposalVideo = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`${BASE}/proposals/upload-video/`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// V2 reporting: proposal-to-listing conversion summary (+ per-agent for admins).
export const getProposalReport = (params = {}) => api.get(`${BASE}/proposals/report/`, { params });

// V3 white-label branding.
export const getProposalBrandings = () => api.get(`${BASE}/proposal-branding/`);
export const createProposalBranding = (data) => api.post(`${BASE}/proposal-branding/`, data);
export const updateProposalBranding = (id, data) => api.patch(`${BASE}/proposal-branding/${seg(id)}/`, data);
export const deleteProposalBranding = (id) => api.delete(`${BASE}/proposal-branding/${seg(id)}/`);

// Proposal templates
export const getProposalTemplates = (params = {}) =>
  api.get(`${BASE}/proposal-templates/`, { params });

export const getProposalMergeTags = () => api.get(`${BASE}/proposal-templates/merge-tags/`);

export const createProposalTemplate = (data) =>
  api.post(`${BASE}/proposal-templates/`, data);

export const updateProposalTemplate = (id, data) =>
  api.patch(`${BASE}/proposal-templates/${seg(id)}/`, data);

export const deleteProposalTemplate = (id) =>
  api.delete(`${BASE}/proposal-templates/${seg(id)}/`);

export const duplicateProposalTemplate = (id, data = {}) =>
  api.post(`${BASE}/proposal-templates/${seg(id)}/duplicate/`, data);

// Public (no auth) — the token in the URL is the credential.
export const getPublicProposal = (token) => api.get(`${BASE}/public/proposals/${seg(token)}/`);

export const acceptPublicProposal = (token, data) =>
  api.post(`${BASE}/public/proposals/${seg(token)}/accept/`, data);

export const requestProposalCallback = (token, data) =>
  api.post(`${BASE}/public/proposals/${seg(token)}/callback/`, data);

export const downloadPublicProposalPdf = (token) =>
  api.get(`${BASE}/public/proposals/${seg(token)}/pdf/`, { responseType: "blob" });
